import { CompactInlineAlertWidget, InlineAlertType } from '@tableau/widgets';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { ReactActionDialogBodyProps } from '~src/commonInfra/dialogs/ReactActionDialog';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { EditConnectionDialogProps, EditConnectionDialogState } from '~src/connectivity/components/EditConnection/EditConnectionAction';

import { EditConnectionDialogBridgeInfo } from './EditConnectionActionDialogBridgeInfo';
import { EditConnectionDialogEmbeddedPasswordInput, EditConnectionDialogEmbeddedToggle } from './EditConnectionActionDialogEmbeddedToggle';
import { EditConnectionDialogPrompt } from './EditConnectionActionDialogPrompt';
import { EditConnectionDialogTestConnection } from './EditConnectionActionDialogTestConnection';
import { EditConnectionDialogValueRow } from './EditConnectionActionDialogValueRow';

export type EditConnectionDialogBodyReactActionProps = ReactActionDialogBodyProps<
  EditConnectionDialogProps,
  EditConnectionDialogState
>;

export function EditConnectionActionDialogBody(props: EditConnectionDialogBodyReactActionProps) {
  function updateFormState<K extends keyof EditConnectionDialogState>(
    field: K,
    value: EditConnectionDialogState[K]
  ) {
    props.updateDialogState({ [field]: value });
  }

  return (
    <>
      <EditConnectionDialogPrompt prompt={props.prompt} />
      {/* W-19373381: client-side password encryption was removed from this flow; the password is
          now sent plaintext over TLS. On insecure (HTTP) installs this surfaces the Security-required
          warning that credentials are transmitted unprotected. Gated by a flag so the warning can be
          rolled out independently of the fix. */}
      {FeatureFlags.insecureConnectionCredentialWarningEnabled && window.isSecureContext === false ? (
        <CompactInlineAlertWidget
          children={Messages.insecureConnectionCredentialWarning()}
          type={InlineAlertType.Warning}
          testId={'edit-conn-insecure-warning'}
        />
      ) : null}
      <div style={{ width: '100%' }}>
        <EditConnectionDialogValueRow
          labelText={Messages.serverName()}
          valueText={props.dialogState.serverName}
          isDisabled={props.isServerNameDisabled}
          testId={'edit-conn-server-name'}
          maxLength={255}
          handleChange={(value) => updateFormState('serverName', value)}
        />

        <EditConnectionDialogValueRow
          labelText={Messages.serverPort()}
          valueText={props.dialogState.serverPort}
          isDisabled={props.isServerPortDisabled}
          isValid={props.isPortValid(props.dialogState.serverPort)}
          testId={'edit-conn-server-port'}
          handleChange={(value) => updateFormState('serverPort', value)}
        />

        {props.isUsernameVisible ? (
          <EditConnectionDialogValueRow
            labelText={Messages.username()}
            valueText={props.dialogState.username}
            isDisabled={false}
            testId={'edit-conn-username'}
            maxLength={255}
            handleChange={(value) => updateFormState('username', value)}
          />
        ) : null
        }

        {props.isEmbeddedToggleVisible ? (
          <EditConnectionDialogEmbeddedToggle
            isPasswordEmbedded={props.dialogState.isPasswordEmbedded}
            handleToggleChange={(value) => updateFormState('isPasswordEmbedded', value)}
            password={props.dialogState.newPassword}
            handlePasswordChange={(value) => updateFormState('newPassword', value)}
          />
        ) : null
        }

        {props.dialogState.isPasswordEmbedded ? (
          <EditConnectionDialogEmbeddedPasswordInput
            isPasswordEmbedded={props.dialogState.isPasswordEmbedded}
            handleToggleChange={(value) => updateFormState('isPasswordEmbedded', value)}
            password={props.dialogState.newPassword}
            handlePasswordChange={(value) => updateFormState('newPassword', value)}
          />
        ) : null
        }

        {props.isTestConnectionButtonVisible ? (
          <EditConnectionDialogTestConnection
            dialogState={props.dialogState}
            onTestConnection={props.onTestConnection}
          />
        ) : null
        }

        <EditConnectionDialogBridgeInfo
          usingRemoteQueryAgent={props.dialogState.usingRemoteQueryAgent}
          handleUsingRemoteQueryAgentChange={(value) => updateFormState('usingRemoteQueryAgent', value)}
          showNetworkType={props.showNetworkType}
        />
      </div>
    </>
  );
}
